/**
 * VP Sticky Add-to-Cart — shows a slim bar once the main product form
 * scrolls out of view, mirrors variant selection, and adds to cart via
 * the Cart API (works with both drawer and notification cart types).
 */
(function () {
  if (customElements.get('vp-sticky-atc')) return;

  class VpStickyAtc extends HTMLElement {
    connectedCallback() {
      this.form = document.getElementById(this.getAttribute('data-form-id'));
      this.mainSubmit = this.form ? this.form.querySelector('[type="submit"]') : null;
      this.observedTarget = document.querySelector(this.getAttribute('data-observe-selector'));

      this.submitBtn = this.querySelector('.vp-sticky-atc__button');
      this.variantSelect = this.querySelector('.vp-sticky-atc__variant-select');
      this.priceEl = this.querySelector('.vp-sticky-atc__price');

      if (this.submitBtn) {
        this.submitBtn.addEventListener('click', this.handleAddToCart.bind(this));
      }

      if (this.variantSelect) {
        this.variantSelect.addEventListener('change', this.handleVariantChange.bind(this));
      }

      if (this.observedTarget && 'IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              // Show sticky bar when the main ATC area is OUT of view
              this.classList.toggle('is-visible', !entry.isIntersecting);
            });
          },
          { rootMargin: '0px 0px -40% 0px', threshold: 0 }
        );
        this.observer.observe(this.observedTarget);
      }

      // Keep sticky variant select in sync if main form changes variant
      document.addEventListener('change', (e) => {
        if (!this.variantSelect) return;
        if (e.target.name === 'id' || (e.target.closest && e.target.closest('variant-radios, variant-selects'))) {
          this.syncFromMainForm();
        }
      });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    syncFromMainForm() {
      if (!this.form || !this.variantSelect) return;
      const idInput = this.form.querySelector('[name="id"]');
      if (idInput && this.variantSelect.value !== idInput.value) {
        this.variantSelect.value = idInput.value;
        this.updatePriceFromOption();
      }
    }

    handleVariantChange() {
      if (!this.form) return;
      const idInput = this.form.querySelector('[name="id"]');
      if (idInput) {
        idInput.value = this.variantSelect.value;
        idInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
      this.updatePriceFromOption();
    }

    updatePriceFromOption() {
      if (!this.priceEl || !this.variantSelect) return;
      const option = this.variantSelect.options[this.variantSelect.selectedIndex];
      const price = option.getAttribute('data-price');
      const compare = option.getAttribute('data-compare-price');
      if (price) {
        let html = price;
        if (compare && compare !== price) {
          html += `<span class="vp-sticky-atc__compare">${compare}</span>`;
        }
        this.priceEl.innerHTML = html;
      }
    }

    handleAddToCart(e) {
      e.preventDefault();
      if (!this.form) return;

      const idInput = this.form.querySelector('[name="id"]');
      const variantId = idInput ? idInput.value : null;
      if (!variantId) return;

      this.submitBtn.setAttribute('aria-busy', 'true');
      this.submitBtn.disabled = true;
      const originalText = this.submitBtn.textContent;
      this.submitBtn.textContent = '...';

      const body = JSON.stringify({
        items: [{ id: variantId, quantity: 1 }],
        sections: this.getSectionsToRender(),
        sections_url: window.location.pathname,
      });

      fetch(window.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body,
      })
        .then((res) => res.json())
        .then((data) => {
          this.submitBtn.textContent = originalText;
          this.submitBtn.removeAttribute('aria-busy');
          this.submitBtn.disabled = false;

          if (data.status) {
            // error state — fall back to native form submit
            this.form.requestSubmit ? this.form.requestSubmit() : this.form.submit();
            return;
          }

          // Update cart icon bubble count
          this.updateCartBubble(data);

          // Open cart drawer if present, else notification, else go to cart
          const drawer = document.querySelector('cart-drawer');
          const notification = document.querySelector('cart-notification');

          if (drawer && typeof drawer.renderContents === 'function' && data.sections) {
            drawer.renderContents(data);
          } else if (notification && typeof notification.renderContents === 'function' && data.sections) {
            notification.renderContents(data);
          } else {
            window.location.href = window.routes.cart_url;
          }
        })
        .catch(() => {
          this.submitBtn.textContent = originalText;
          this.submitBtn.removeAttribute('aria-busy');
          this.submitBtn.disabled = false;
          this.form.requestSubmit ? this.form.requestSubmit() : this.form.submit();
        });
    }

    getSectionsToRender() {
      const drawer = document.querySelector('cart-drawer');
      const notification = document.querySelector('cart-notification');
      const target = drawer || notification;
      if (target && typeof target.getSectionsToRender === 'function') {
        return target.getSectionsToRender().map((s) => s.id);
      }
      return [];
    }

    updateCartBubble(data) {
      const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
      const icon = document.querySelector('#cart-icon-bubble');
      if (!icon) return;
      fetch(window.routes.cart_url + '.js')
        .then((res) => res.json())
        .then((cart) => {
          if (bubble) {
            const span = bubble.querySelector('span[aria-hidden]');
            if (span) span.textContent = cart.item_count;
          }
        })
        .catch(() => {});
    }
  }

  customElements.define('vp-sticky-atc', VpStickyAtc);
})();
